import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertOctagon, Search, Plus, RefreshCcw, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, X, XCircle, LayoutGrid, Shield, Layers, Clock, List, LayoutList, Users, FileText, Ban, UserX, PauseCircle, MessageSquare, DollarSign } from 'lucide-react';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { normalizeSanctionRecord } from '../../shared/utils/hrApiRecordNormalizers';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  buildHrImportExportProps,
  buildHrControlsStrip,
  isHrColumnVisible,
  createHrHandleToggleColumn,
  hrTableColSpan,
  useHrModalBodyLock,
  HrFormModalTabBar,
  useHrTableRowSelection,
  HrBulkDeleteFilterAction,
  buildHrEmployeeSelectOptions,
  buildHrEmployeePickerPatch,
  resolveHrEmployeeFromLabel,
  resolveHrEmployeeMatricule,
  useHrEmployees,
  sliceHrListItems,
} from './hrListPageUtils';
import { useHrConfig } from './useHrConfig';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
registerLocale('ar', ar);

const HR_TABLE_COLUMNS = [
  { key: 'sanction_number', labelKey: 'col_sanction_number' },
  { key: 'employee_name', labelKey: 'label_employee' },
  { key: 'incident_date', labelKey: 'col_incident_date' },
  { key: 'incident_type', labelKey: 'col_incident_type' },
  { key: 'severity', labelKey: 'label_severity' },
  { key: 'suspension_days', labelKey: 'col_suspension' },
  { key: 'status', labelKey: 'label_status' },
  { key: 'actions', required: true },
];

const NS = 'modules.ressources_humaines.pages.sanctionsPage';

const SanctionsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const { settings } = useSettings();
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const hrConfig = useHrConfig();

  const fmtDate = (val) => (val ? new Date(val).toLocaleDateString(dateLocale) : p('na'));
  const [searchTerm, setSearchTerm] = useState('');
  const [sanctions, setSanctions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  
  // Live employee picker — refreshes on HR_EMPLOYEES_REFRESH_EVENT
  const { employees: realEmployees } = useHrEmployees();
  
  const [formTab, setFormTab] = useState('sanction');
  
  const [formData, setFormData] = useState({ 
    sanction_number: '',
    employee_id: '',
    employee_name: '',
    incident_date: '',
    sanction_date: '',
    incident_type: '',
    severity: '',
    sanction_description: '',
    witness_names: '',
    status: 'en_attente',
    appeal_date: '',
    appeal_result: '',
    suspension_days: '',
    suspension_start: '',
    suspension_end: '',
    penalty_amount: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_sanctions');
  const [quickFilter, setQuickFilter] = useState('all');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  useEffect(() => { loadSanctions(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStatusLabel = useCallback((status) => {
    const labels = {
      en_attente: p('status_en_attente'),
      appliquée: p('status_appliquee'),
      contestée: p('status_contestee'),
      annulée: p('status_annulee')
    };
    return labels[status] || p('empty_dash');
  }, [p]);

  const loadSanctions = async () => {
    try {
      const response = await localApiAdapter.get('/hr/sanctions');
      const raw = response?.data ?? response ?? [];
      const list = Array.isArray(raw) ? raw : [];
      setSanctions(sortRecordsNewestFirst(list.map(normalizeSanctionRecord)));
    } catch (error) {
      console.error('Error loading sanctions:', error);
      setSanctions([]);
    }
  };

  const generateSanctionNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'sanction', 'SAN');
    return generateEntityReference(prefix, sanctions, (s) => s.sanction_number);
  }, [sanctions, settings]);

  const stats = {
    total: sanctions.length,
    en_attente: sanctions.filter(s => s.status === 'en_attente').length,
    appliquees: sanctions.filter(s => s.status === 'appliquée').length,
    contestees: sanctions.filter(s => s.status === 'contestée').length,
    suspensions: sanctions.filter(s => s.severity === 'suspension').length
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_sanctions', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_gravite', name: p('tab_by_severity'), icon: AlertTriangle },
    { id: 'par_type', name: p('tab_by_type'), icon: Layers }
  ]), [p]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quick_all'), icon: Layers },
    { id: 'en_attente', name: p('quick_en_attente'), icon: Clock },
    { id: 'appliquée', name: p('quick_appliquees'), icon: CheckCircle },
    { id: 'contestée', name: p('quick_contestees'), icon: AlertCircle },
    { id: 'suspension', name: p('quick_suspensions'), icon: PauseCircle }
  ]), [p]);

  const sanctionsDistribution = useMemo(() => ({
    all_sanctions: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'verbal', name: p('dist_verbal'), count: sanctions.filter(s => s.severity === 'verbal').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'ecrit', name: p('dist_ecrit'), count: sanctions.filter(s => s.severity === 'écrit').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'avertissement', name: p('dist_avertissement'), count: sanctions.filter(s => s.severity === 'avertissement').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'deduction', name: p('dist_deduction'), count: sanctions.filter(s => s.severity === 'deduction').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'suspension', name: p('dist_suspension'), count: sanctions.filter(s => s.severity === 'suspension').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'licenciement', name: p('dist_licenciement'), count: sanctions.filter(s => s.severity === 'licenciement').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' }
      ]
    },
    par_gravite: {
      title: p('dist_by_severity'),
      data: [
        { filterKey: 'verbal', name: p('dist_verbal'), count: sanctions.filter(s => s.severity === 'verbal').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'ecrit', name: p('dist_ecrit'), count: sanctions.filter(s => s.severity === 'écrit').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'avertissement', name: p('dist_avertissement'), count: sanctions.filter(s => s.severity === 'avertissement').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'deduction', name: p('dist_deduction'), count: sanctions.filter(s => s.severity === 'deduction').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'suspension', name: p('dist_suspension'), count: sanctions.filter(s => s.severity === 'suspension').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'licenciement', name: p('dist_licenciement'), count: sanctions.filter(s => s.severity === 'licenciement').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type'),
      data: [
        { filterKey: 'retard', name: p('dist_retard'), count: sanctions.filter(s => s.incident_type === 'retard').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'absence', name: p('dist_absence'), count: sanctions.filter(s => s.incident_type === 'absence_injustifiée').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'insubordination', name: p('dist_insubordination'), count: sanctions.filter(s => s.incident_type === 'insubordination').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'vol', name: p('dist_vol'), count: sanctions.filter(s => s.incident_type === 'vol').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'violence', name: p('dist_violence'), count: sanctions.filter(s => s.incident_type === 'violence').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'negligence', name: p('dist_negligence'), count: sanctions.filter(s => s.incident_type === 'négligence').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    }
  }), [p, sanctions]);

  const distributionInfo = sanctionsDistribution[mainViewMode] || sanctionsDistribution.all_sanctions;

  const baseFilteredSanctions = sanctions.filter(s => {
    if (searchTerm && !s.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !s.sanction_number?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_sanctions') {
      if (quickFilter === 'en_attente' && s.status !== 'en_attente') return false;
      if (quickFilter === 'appliquée' && s.status !== 'appliquée') return false;
      if (quickFilter === 'contestée' && s.status !== 'contestée') return false;
      if (quickFilter === 'suspension' && s.severity !== 'suspension') return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_sanctions':
        case 'par_gravite':
          if (activeDistributionFilter === 'verbal' && s.severity !== 'verbal') return false;
          if (activeDistributionFilter === 'ecrit' && s.severity !== 'écrit') return false;
          if (activeDistributionFilter === 'avertissement' && s.severity !== 'avertissement') return false;
          if (activeDistributionFilter === 'deduction' && s.severity !== 'deduction') return false;
          if (activeDistributionFilter === 'suspension' && s.severity !== 'suspension') return false;
          if (activeDistributionFilter === 'licenciement' && s.severity !== 'licenciement') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'retard' && s.incident_type !== 'retard') return false;
          if (activeDistributionFilter === 'absence' && s.incident_type !== 'absence_injustifiée') return false;
          if (activeDistributionFilter === 'insubordination' && s.incident_type !== 'insubordination') return false;
          if (activeDistributionFilter === 'vol' && s.incident_type !== 'vol') return false;
          if (activeDistributionFilter === 'violence' && s.incident_type !== 'violence') return false;
          if (activeDistributionFilter === 'negligence' && s.incident_type !== 'négligence') return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('sanction_number', (r) => r.sanction_number || ''),
    textColumnRule('employee_name', (r) => r.employee_name || ''),
    textColumnRule('incident_date', (r) => (r.incident_date ? new Date(r.incident_date).toLocaleDateString(dateLocale) : '')),
    textColumnRule('severity', (r) => r.severity || ''),
    textColumnRule('suspension_days', (r) => String(r.suspension_days ?? '')),
  ], [dateLocale]);

  const filteredSanctions = useMemo(
    () => sliceHrListItems(
      applyColumnFilters(baseFilteredSanctions, colFilters, columnFilterRules),
      hrConfig.itemsPerPage,
    ),
    [baseFilteredSanctions, colFilters, columnFilterRules, hrConfig.itemsPerPage],
  );


  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredSanctions, selectedRecords, setSelectedRecords);
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = [
      { field: 'employee_name', label: p('col_employee_name') },
      { field: 'incident_date', label: p('col_incident_date') },
      { field: 'sanction_date', label: p('col_sanction_date') },
      { field: 'incident_type', label: p('col_incident_type') },
      { field: 'severity', label: p('label_severity') }
    ];
    
    const missingFields = requiredFields.filter(
      ({ field }) => !formData[field] || formData[field].trim() === ''
    );
    
    if (missingFields.length > 0) {
      const missingLabels = missingFields.map(({ label }) => label).join(', ');
      alert(p('alert_required_fields', { fields: missingLabels }));
      return;
    }
    
    try {
      const sanctionNumber = (formData.sanction_number || '').trim() || generateSanctionNumber();
      const submitData = { 
        ...formData,
        sanction_number: sanctionNumber,
        employee_id: formData.employee_id ? String(formData.employee_id) : null,
        suspension_days: parseInt(formData.suspension_days) || 0,
        penalty_amount: parseFloat(formData.penalty_amount) || 0
      };
      if (editingRecord) {
        await localApiAdapter.put(`/hr/sanctions/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الجزاء' }));
      } else {
        await localApiAdapter.post('/hr/sanctions', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الجزاء' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('na') }));
      return;
    } finally {
      resetForm();
      loadSanctions();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      sanction_number: record.sanction_number || '',
      employee_id: record.employee_id?.toString() || '',
      employee_name: record.employee_name || '',
      incident_date: record.incident_date || '',
      sanction_date: record.sanction_date || '',
      incident_type: record.incident_type || '',
      severity: record.severity || '',
      sanction_description: record.sanction_description || '',
      witness_names: record.witness_names || '',
      status: record.status || 'en_attente',
      appeal_date: record.appeal_date || '',
      appeal_result: record.appeal_result || '',
      suspension_days: record.suspension_days?.toString() || '',
      suspension_start: record.suspension_start || '',
      suspension_end: record.suspension_end || '',
      penalty_amount: record.penalty_amount?.toString() || '',
      notes: record.notes || ''
    });
    setFormTab('sanction');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/hr/sanctions/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الجزاء' }));
        loadSanctions();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_delete_error', { defaultValue: 'تعذّر حذف الجزاء' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/hr/sanctions/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'جزاء' }));
        setSelectedRecords([]);
        loadSanctions();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_bulk_delete_error', { defaultValue: 'تعذّر حذف الجزاءات المحددة' }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      sanction_number: '', employee_id: '', employee_name: '', incident_date: '', sanction_date: '',
      incident_type: '', severity: '', sanction_description: '', witness_names: '',
      status: 'en_attente', appeal_date: '', appeal_result: '', suspension_days: '',
      suspension_start: '', suspension_end: '', penalty_amount: '', notes: ''
    });
    setEditingRecord(null);
    setFormTab('sanction');
    setShowModal(false);
  };

  const openNewSanctionModal = () => {
    setEditingRecord(null);
    setFormData({
      sanction_number: generateSanctionNumber(),
      employee_id: '',
      employee_name: '',
      incident_date: '',
      sanction_date: '',
      incident_type: '',
      severity: '',
      sanction_description: '',
      witness_names: '',
      status: 'en_attente',
      appeal_date: '',
      appeal_result: '',
      suspension_days: '',
      suspension_start: '',
      suspension_end: '',
      penalty_amount: '',
      notes: ''
    });
    setFormTab('sanction');
    setShowModal(true);
  };

  const handleExport = () => {
    const headers = [
      { key: 'sanction_number', label: p('col_sanction_number') },
      { key: 'employee_name', label: p('label_employee') },
      { key: 'incident_date', label: p('col_incident_date') },
      { key: 'sanction_date', label: p('col_sanction_date') },
      { key: 'incident_type', label: p('col_incident_type') },
      { key: 'severity', label: p('label_severity') },
      { key: 'status', label: p('label_status') },
      { key: 'suspension_days', label: p('col_suspension_days') },
      { key: 'penalty_amount', label: p('col_penalty_amount') }
    ];
    const fileName = `sanctions_export_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToCSV(filteredSanctions, headers, `${fileName}.csv`);
    } catch (error) {
      console.error('Export error:', error);
      alert(p('alert_export_error'));
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `<div class="report-content"><table><thead><tr><th>${p('label_employee')}</th><th>${p('label_date')}</th><th>${p('label_type')}</th><th>${p('label_severity')}</th><th>${p('label_status')}</th></tr></thead><tbody>${filteredSanctions.map(item => `<tr><td>${item.employee_name || p('na')}</td><td>${fmtDate(item.incident_date)}</td><td>${item.incident_type || p('na')}</td><td>${item.severity || p('na')}</td><td>${item.status || p('na')}</td></tr>`).join('')}</tbody></table></div>`;
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `<div class="report-content"><table><thead><tr><th>${p('col_sanction_number')}</th><th>${p('col_employee_name')}</th><th>${p('col_incident_date')}</th><th>${p('col_sanction_date')}</th><th>${p('col_incident_type')}</th><th>${p('label_severity')}</th><th>${p('label_status')}</th><th>${p('col_suspension_days')}</th><th>${p('col_penalty_amount')}</th></tr></thead><tbody>${filteredSanctions.map(item => `<tr><td>${item.sanction_number || ''}</td><td>${item.employee_name || ''}</td><td>${item.incident_date ? new Date(item.incident_date).toLocaleDateString(dateLocale) : ''}</td><td>${item.sanction_date ? new Date(item.sanction_date).toLocaleDateString(dateLocale) : ''}</td><td>${item.incident_type || ''}</td><td>${item.severity || ''}</td><td>${item.status || ''}</td><td>${item.suspension_days || '0'}</td><td>${item.penalty_amount ? formatBrandMoney(item.penalty_amount) : ''}</td></tr>`).join('')}</tbody></table></div>`;
      document.body.appendChild(tempDiv);
      await generateReportExcel('sanctions', p('report_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('Excel generation error:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'sanction_number', label: p('col_sanction_number') },
      { key: 'employee_name', label: p('label_employee') },
      { key: 'incident_date', label: p('col_incident_date') },
      { key: 'sanction_date', label: p('col_sanction_date') },
      { key: 'incident_type', label: p('col_incident_type') },
      { key: 'severity', label: p('label_severity') },
      { key: 'status', label: p('label_status') }
    ];

    const schema = {
      requiredFields: ['sanction_number', 'employee_name', 'incident_date', 'sanction_date', 'incident_type', 'severity'],
      validators: {
        severity: (value) => ['verbal', 'écrit', 'avertissement', 'deduction', 'suspension', 'licenciement'].includes(value),
        status: (value) => !value || ['en_attente', 'appliquée', 'contestée', 'annulée'].includes(value)
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/hr/sanctions', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadSanctions();
          let message = p('alert_import_success', { imported: successCount, total: result.totalCount });
          if (result.invalidCount > 0) {
            message += '\n' + p('alert_import_errors', { count: result.invalidCount, details: result.errors.slice(0, 5).join('\n') });
          }
          alert(message);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };
  const statusOptions = useMemo(() => ([
    { value: 'en_attente', label: p('status_en_attente'), icon: Clock, style: 'bg-blue-500/20 text-orange-400 border-blue-500/30' },
    { value: 'appliquée', label: p('status_appliquee'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'contestée', label: p('status_contestee'), icon: AlertCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'annulée', label: p('status_annulee'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
  ]), [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/hr/sanctions/${recordId}`, { status: newStatus });
      await loadSanctions();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const qawaemStatusOptions = useMemo(
    () => [{ value: 'all', label: p('quick_all') }, ...statusOptions.map((s) => ({ value: s.value, label: s.label }))],
    [statusOptions, p],
  );
  const listStats = useMemo(() => [
    { key: 'total', label: p('stat_total'), value: stats.total, icon: AlertOctagon, variant: 'copper' },
    { key: 'pending', label: p('stat_en_attente'), value: stats.en_attente, icon: Clock, variant: 'forest' },
    { key: 'applied', label: p('stat_appliquees'), value: stats.appliquees, icon: CheckCircle, variant: 'copper' },
    { key: 'contested', label: p('stat_contestees'), value: stats.contestees, icon: AlertCircle, variant: 'forest' },
  ], [stats, p]);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(
      HR_TABLE_COLUMNS.map((col) => (
        col.key === 'actions' ? { ...col, label: t('common.actions') } : col
      )),
      p,
      columnVisibility,
    ),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);
  const importExportProps = useMemo(() => buildHrImportExportProps({
    showImportDropdown, setShowImportDropdown, showExportDropdown, setShowExportDropdown,
    importDropdownRef, exportDropdownRef, p, t, isGeneratingPDF,
    handlers: { onImport: handleImport, onExportCSV: handleExport, onExportExcel: handleExportExcel, onExportPDF: handleExportPDF },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t]);
  const sanctionFormTabs = useMemo(() => ([
    { id: 'sanction', label: p('section_sanction'), icon: AlertOctagon, iconClassName: 'text-blue-400' },
    { id: 'followup', label: p('section_suspension'), icon: PauseCircle, iconClassName: 'text-amber-400' },
  ]), [p]);

  const advancedOptionsContent = (
    <div className="space-y-6 pt-0">
<div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_sanctions' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
                <button key={item.filterKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-orange-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`} title={p('filter_click_title', { name: item.name })}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('pct_of_sanctions', { pct: (() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })() })}</p>
                </button>
              ))}
        </div>
      </motion.div>
    </div>
  );


  const getSeverityBadge = useCallback((severity) => {
    const badges = {
      verbal: { label: p('severity_verbal'), style: 'bg-green-500/20 text-green-400 border-green-500/30', icon: MessageSquare },
      écrit: { label: p('severity_ecrit'), style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: FileText },
      avertissement: { label: p('severity_avertissement'), style: 'bg-blue-500/20 text-orange-400 border-blue-500/30', icon: AlertTriangle },
      deduction: { label: p('severity_deduction'), style: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: DollarSign },
      suspension: { label: p('severity_suspension'), style: 'bg-red-500/20 text-red-400 border-red-500/30', icon: PauseCircle },
      licenciement: { label: p('severity_licenciement'), style: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: UserX }
    };
    const config = badges[severity] || badges.verbal;
    const Icon = config.icon;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${config.style}`}><Icon size={14} />{config.label}</span>;
  }, [p]);

  const getTypeBadge = useCallback((type) => {
    const badges = {
      retard: { label: p('incident_retard'), style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      absence_injustifiée: { label: p('incident_absence'), style: 'bg-blue-500/20 text-orange-400 border-blue-500/30' },
      insubordination: { label: p('incident_insubordination'), style: 'bg-red-500/20 text-red-400 border-red-500/30' },
      vol: { label: p('incident_vol'), style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
      violence: { label: p('incident_violence'), style: 'bg-red-500/20 text-red-400 border-red-500/30' },
      négligence: { label: p('incident_negligence'), style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      autre: { label: p('incident_autre'), style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
    };
    const config = badges[type] || badges.autre;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${config.style}`}>{config.label}</span>;
  }, [p]);

    return (
    <>
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={AlertOctagon}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={
          <HrBulkDeleteFilterAction
            count={selectedRecords.length}
            onDelete={handleBulkDelete}
            label={p('delete_count', { count: selectedRecords.length })}
          />
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        columns={qawaemColumns}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumns.length}
        onCreate={openNewSanctionModal}
        createLabel={p('new_item')}
        importExport={importExportProps}
        headerActions={<button type="button" onClick={loadSanctions} className="refresh-button" title={p('refresh_title')}><RefreshCcw size={20} /></button>}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildHrControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        {viewMode === 'table' && (
        <>
        <div className={facturationTableScrollClass(qawaem)}>
          <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox size="lg" checked={allRowsSelected} indeterminate={someRowsSelected} onChange={handleSelectAll} /></div></th>
                  {isColVisible('sanction_number') && (
                  <TableColumnHeaderFilter
                    columnKey="sanction_number"
                    label={p('col_sanction_number')}
                    value={colFilters.sanction_number ?? ''}
                    onChange={(v) => setColFilter('sanction_number', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('employee_name') && (
                  <TableColumnHeaderFilter
                    columnKey="employee_name"
                    label={p('label_employee')}
                    value={colFilters.employee_name ?? ''}
                    onChange={(v) => setColFilter('employee_name', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('incident_date') && (
                  <TableColumnHeaderFilter
                    columnKey="incident_date"
                    label={p('col_incident_date')}
                    value={colFilters.incident_date ?? ''}
                    onChange={(v) => setColFilter('incident_date', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('incident_type') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_incident_type')}</th>}
                  {isColVisible('severity') && (
                  <TableColumnHeaderFilter
                    columnKey="severity"
                    label={p('label_severity')}
                    value={colFilters.severity ?? ''}
                    onChange={(v) => setColFilter('severity', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('suspension_days') && (
                  <TableColumnHeaderFilter
                    columnKey="suspension_days"
                    label={p('col_suspension')}
                    value={colFilters.suspension_days ?? ''}
                    onChange={(v) => setColFilter('suspension_days', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('status') && <th className="px-6 py-4 text-center text-white font-semibold">{p('label_status')}</th>}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredSanctions.length > 0 ? (
                  filteredSanctions.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} /></div></td>
                      {isColVisible('sanction_number') && (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{record.sanction_number || p('na')}</span>
                      </td>
                      )}
                      {isColVisible('employee_name') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.employee_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('matricule_prefix')} {resolveHrEmployeeMatricule(realEmployees, record) || p('na')} | {p('card_number_prefix')} {record.sanction_number} | {p('witness_prefix')} {record.witness_names || p('none')}</span>
                        </div>
                      </td>
                      )}
                      {isColVisible('incident_date') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Calendar size={14} className="text-white/50" />
                            {record.incident_date ? new Date(record.incident_date).toLocaleDateString(dateLocale) : p('empty_dash')}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">{p('sanction_date_prefix')} {record.sanction_date ? new Date(record.sanction_date).toLocaleDateString(dateLocale) : p('empty_dash')}</span>
                        </div>
                      </td>
                      )}
                      {isColVisible('incident_type') && (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getTypeBadge(record.incident_type)}</div></td>
                      )}
                      {isColVisible('severity') && (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getSeverityBadge(record.severity)}</div></td>
                      )}
                      {isColVisible('suspension_days') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.suspension_days || 0} {p('days_suffix')}</span>
                          {(record.penalty_amount != null && record.penalty_amount !== '' && parseFloat(record.penalty_amount) > 0) && (
                            <span className="text-orange-400 text-xs mt-0.5">{p('col_penalty_amount')}: {formatBrandMoney(record.penalty_amount)}</span>
                          )}
                          <span className="text-white/50 text-xs mt-0.5">{record.suspension_start ? `${new Date(record.suspension_start).toLocaleDateString(dateLocale)} - ${record.suspension_end ? new Date(record.suspension_end).toLocaleDateString(dateLocale) : ''}` : p('na')}</span>
                        </div>
                      </td>
                      )}
                      {isColVisible('status') && (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title')} /></div></td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><AlertOctagon size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredSanctions.length > 0 ? (
              filteredSanctions.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2 min-w-0">
                          <AlertOctagon size={20} className="text-orange-400 shrink-0" />
                          <span className="truncate">{record.employee_name}</span>
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {getTypeBadge(record.incident_type)}
                          {getSeverityBadge(record.severity)}
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title')} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_incident_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_number_prefix')}</span> {record.sanction_number}</div>
                            <div><span className="font-medium">{p('label_matricule')}:</span> {resolveHrEmployeeMatricule(realEmployees, record) || p('na')}</div>
                            <div><span className="font-medium">{p('label_incident_date_card')}</span> {record.incident_date ? new Date(record.incident_date).toLocaleDateString(dateLocale) : p('empty_dash')}</div>
                            <div><span className="font-medium">{p('label_sanction_date_card')}</span> {record.sanction_date ? new Date(record.sanction_date).toLocaleDateString(dateLocale) : p('empty_dash')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_type_severity')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_type')}:</span> {record.incident_type}</div>
                            <div><span className="font-medium">{p('label_severity')}:</span> {record.severity}</div>
                            <div><span className="font-medium">{p('label_witnesses')}:</span> {record.witness_names || p('none')}</div>
                            {record.sanction_description && <div className="mt-2 p-2 bg-white/5 rounded"><p className="text-white/60 text-xs">{record.sanction_description}</p></div>}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_suspension')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_duration')}:</span> {record.suspension_days || 0} {p('days_suffix')}</div>
                            {(record.penalty_amount != null && parseFloat(record.penalty_amount) > 0) && (
                              <div><span className="font-medium">{p('label_penalty_amount')}:</span> {formatBrandMoney(record.penalty_amount)}</div>
                            )}
                            <div><span className="font-medium">{p('label_start')}:</span> {record.suspension_start ? new Date(record.suspension_start).toLocaleDateString(dateLocale) : p('empty_dash')}</div>
                            <div><span className="font-medium">{p('label_end')}:</span> {record.suspension_end ? new Date(record.suspension_end).toLocaleDateString(dateLocale) : p('empty_dash')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_appeal')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_date')}:</span> {record.appeal_date ? new Date(record.appeal_date).toLocaleDateString(dateLocale) : p('none')}</div>
                            <div><span className="font-medium">{p('label_result')}:</span> {record.appeal_result || p('na')}</div>
                            {record.notes && <div className="mt-2 p-2 bg-white/5 rounded"><span className="font-medium text-white/80 text-xs">{p('label_notes')}:</span><p className="text-white/60 text-xs mt-1">{record.notes}</p></div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><AlertOctagon size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* View Modal — shell & sections aligned with EmployeesPage view modal */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => { setShowViewModal(false); setViewingRecord(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button type="button" onClick={() => { setShowViewModal(false); setViewingRecord(null); }} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div >
                {/* Référence & employé — same grid density as Employees “Informations Personnelles” */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Users size={18} className="text-blue-400" />
{p('section_sanction_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_sanction_number')}</p>
                      <p className="text-white font-medium">{viewingRecord.sanction_number || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_employee')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.employee_name || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_matricule')}</p>
                      <p className="text-white font-medium">{resolveHrEmployeeMatricule(realEmployees, viewingRecord) || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_incident_date')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.incident_date ? new Date(viewingRecord.incident_date).toLocaleDateString(dateLocale) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_sanction_date')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.sanction_date ? new Date(viewingRecord.sanction_date).toLocaleDateString(dateLocale) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_type')}</p>
                      <div className="flex flex-wrap">{getTypeBadge(viewingRecord.incident_type)}</div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_severity')}</p>
                      <div className="flex flex-wrap">{getSeverityBadge(viewingRecord.severity)}</div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_status')}</p>
                      <div className="flex flex-wrap">
                        <QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title')} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Détails incident — green accent like Employees “Informations Professionnelles” */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-green-400" />
{p('section_incident_details')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-3">
                      <p className="text-white/60 text-sm mb-1">{p('label_description')}</p>
                      <p className="text-white font-medium whitespace-pre-wrap">{viewingRecord.sanction_description || '—'}</p>
                    </div>
                    <div className="md:col-span-1">
                      <p className="text-white/60 text-sm mb-1">{p('label_witnesses')}</p>
                      <p className="text-white font-medium">{viewingRecord.witness_names || p('no_witness')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <PauseCircle size={18} className="text-red-400" />
{p('section_suspension')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_duration')}</p>
                        <p className="text-white font-medium">{viewingRecord.suspension_days || 0} {p('days_suffix')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_penalty_amount')}</p>
                        <p className="text-white font-medium">
                          {viewingRecord.penalty_amount != null && parseFloat(viewingRecord.penalty_amount) > 0
                            ? formatBrandMoney(viewingRecord.penalty_amount)
                            : p('na')}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_start')}</p>
                        <p className="text-white font-medium">
                          {viewingRecord.suspension_start ? new Date(viewingRecord.suspension_start).toLocaleDateString(dateLocale) : p('na')}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_end')}</p>
                        <p className="text-white font-medium">
                          {viewingRecord.suspension_end ? new Date(viewingRecord.suspension_end).toLocaleDateString(dateLocale) : p('na')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Shield size={18} className="text-purple-400" />
{p('section_appeal')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_appeal_date')}</p>
                        <p className="text-white font-medium">
{viewingRecord.appeal_date ? new Date(viewingRecord.appeal_date).toLocaleDateString(dateLocale) : p('appeal_none')}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_result')}</p>
                        <p className="text-white font-medium">{viewingRecord.appeal_result || p('na')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <MessageSquare size={18} className="text-amber-400" />
{p('label_notes')}
                  </h3>
                  <p className="text-white font-medium whitespace-pre-wrap">{viewingRecord.notes || p('none_notes')}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Edit/Create Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit') : p('modal_create')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="sanction-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />{editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            
            <HrFormModalTabBar
              tabs={sanctionFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="sanction-form" onSubmit={handleSubmit} >
                {formTab === 'sanction' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Row 1: N° Sanction, Nom Employé, Date Incident */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_sanction_number')} <span className="qawaem-required-mark">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.sanction_number}
                              onChange={(e) => setFormData({ ...formData, sanction_number: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('ph_sanction_number')}
                            />
                            {!editingRecord && (
                              <p className="text-white/50 text-xs mt-1">{p('hint_auto_number')}</p>
                            )}
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_employee_name')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.employee_name}
                              onChange={(value) => {
                                const selectedEmployee = resolveHrEmployeeFromLabel(realEmployees, value);
                                if (selectedEmployee) {
                                  setFormData({ ...formData, ...buildHrEmployeePickerPatch(selectedEmployee) });
                                } else {
                                  setFormData({ ...formData, employee_name: value, employee_id: '' });
                                }
                              }}
                              options={buildHrEmployeeSelectOptions(realEmployees)}
                              placeholder={p('placeholder_select_employee')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('hint_employee_select')}</p>
                          </div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_incident_date')} <span className="qawaem-required-mark">*</span></label><CustomDatePicker value={formData.incident_date} onChange={(value) => setFormData({ ...formData, incident_date: value })} placeholder={p('placeholder_date')} /></div>
                        </div>
                        
                        {/* Row 2: Date Sanction, Type Incident, Gravité, Statut */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_sanction_date')} <span className="qawaem-required-mark">*</span></label><CustomDatePicker value={formData.sanction_date} onChange={(value) => setFormData({ ...formData, sanction_date: value })} placeholder={p('placeholder_date')} /></div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_incident_type')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.incident_type}
                              onChange={(value) => setFormData({ ...formData, incident_type: value })}
                              options={[
                                { value: 'retard', label: p('incident_retard') },
                                { value: 'absence_injustifiée', label: p('incident_absence_full') },
                                { value: 'insubordination', label: p('incident_insubordination') },
                                { value: 'vol', label: p('incident_vol') },
                                { value: 'violence', label: p('incident_violence') },
                                { value: 'négligence', label: p('incident_negligence') },
                                { value: 'autre', label: p('incident_autre') }
                              ]}
                              placeholder={p('placeholder_select_type')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('hint_select_type')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_severity')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.severity}
                              onChange={(value) => setFormData({ ...formData, severity: value })}
                              options={[
                                { value: 'verbal', label: p('severity_verbal') },
                                { value: 'écrit', label: p('severity_ecrit') },
                                { value: 'avertissement', label: p('severity_avertissement') },
                                { value: 'deduction', label: p('severity_deduction') },
                                { value: 'suspension', label: p('severity_suspension') },
                                { value: 'licenciement', label: p('severity_licenciement') }
                              ]}
                              placeholder={p('placeholder_select_severity')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('hint_select_severity')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'en_attente', label: p('status_en_attente') },
                                { value: 'appliquée', label: p('status_appliquee') },
                                { value: 'contestée', label: p('status_contestee') },
                                { value: 'annulée', label: p('status_annulee') }
                              ]}
                              placeholder={p('placeholder_select_status')}
                            />
                          </div>
                        </div>
                    </div>
                  </>
                )}
                {formTab === 'sanction' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_sanction_desc')}</label><textarea value={formData.sanction_description} onChange={(e) => setFormData({ ...formData, sanction_description: e.target.value })} rows="3" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('placeholder_sanction_desc')} /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_witness_names')}</label><input type="text" value={formData.witness_names} onChange={(e) => setFormData({ ...formData, witness_names: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_witnesses')} /></div>
                    </div>
                  </>
                )}
                {formTab === 'followup' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_suspension_days')}</label><CustomNumberInput value={formData.suspension_days} onChange={(value) => setFormData({ ...formData, suspension_days: value })} placeholder="0" /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_penalty_amount')}</label><CustomNumberInput value={formData.penalty_amount} onChange={(value) => setFormData({ ...formData, penalty_amount: value })} placeholder={p('placeholder_penalty')} /><p className="text-white/50 text-xs mt-1">{p('penalty_law_hint')}</p></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_suspension_start')}</label><CustomDatePicker value={formData.suspension_start} onChange={(value) => setFormData({ ...formData, suspension_start: value })} placeholder={p('placeholder_date')} /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_suspension_end')}</label><CustomDatePicker value={formData.suspension_end} onChange={(value) => setFormData({ ...formData, suspension_end: value })} placeholder={p('placeholder_date')} /></div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_appeal_date')}</label><CustomDatePicker value={formData.appeal_date} onChange={(value) => setFormData({ ...formData, appeal_date: value })} placeholder={p('placeholder_date')} /></div>
                        <div className="md:col-span-3">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_appeal_result')}</label>
                          <CustomSelect
                            value={formData.appeal_result}
                            onChange={(value) => setFormData({ ...formData, appeal_result: value })}
                            options={[
                              { value: 'accepté', label: p('appeal_accepte') },
                              { value: 'rejeté', label: p('appeal_rejete') },
                              { value: 'en_cours', label: p('appeal_en_cours') }
                            ]}
                            placeholder={p('placeholder_select_result')}
                            searchable={true}
                            creatable={true}
                          />
                        </div>
                    </div>
                  </>
                )}
                {formTab === 'followup' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="md:col-span-3">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                        <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="4" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('placeholder_notes')} />
                      </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default SanctionsPage;

